import { useEffect, useState, useRef } from "react";
import "./CustomersRequests.css";
import Layout from "../../components/Layout";
import Avatar from "@mui/material/Avatar";
import PageHeader from "../../components/PageHeader/PageHeader";
import DataTable from "../../components/DataTable/DataTable";
import TablePagination from "../../components/DataTable/TablePagination";
import useCachedFetch from "../../hooks/useCachedFetch";
import RequestDetailModal from "./RequestDetailsModal";

const PAGE_SIZE = 15;

/* ─── Status config ─── */
const statusConfig = {
  open: { label: "Open", className: "status-open" },
  in_progress: { label: "In Progress", className: "status-in-progress" },
  reviewing: { label: "Reviewing Offers", className: "status-reviewing" },
  resolved: { label: "Completed", className: "status-resolved" },
  closed: { label: "Closed", className: "status-closed" },
};

const statusIcons = {
  open: "check_circle",
  in_progress: "pending",
  reviewing: "rate_review",
  resolved: "task_alt",
};

const statusFilters = [
  "open",
  "in_progress",
  "reviewing",
  "resolved",
  "closed",
];

/* ─── Helpers ─── */
const formatDate = (timestamp) => {
  if (!timestamp) return "—";
  let date;
  if (timestamp._seconds) date = new Date(timestamp._seconds * 1000);
  else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
  else date = new Date(timestamp);
  if (isNaN(date.getTime())) return "—";
  const month = date.toLocaleString("en-GB", { month: "short" });
  const day = String(date.getDate()).padStart(2, "0");
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { date: `${month} ${day}, ${date.getFullYear()}`, time };
};

const formatBudget = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return val || "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
};

const SKILLS_VISIBLE = 2;

const isOnline = (type) => type === "online" || type === "Online";

/* Renders a { date, time } pair, or a dash */
const DateCell = ({ value }) =>
  value && typeof value === "object" ? (
    <div className="req-cell">
      <div className="date-primary">{value.date}</div>
      <div className="date-time">{value.time}</div>
    </div>
  ) : (
    <span className="no-val">—</span>
  );

/* ═══════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════ */
const CustomersRequests = () => {
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [expandedSkills, setExpandedSkills] = useState({});

  const [filters, setFilters] = useState({
    status: "open",
    category: "all",
    subCategory: "all",
    location: "all",
    sort: "latest",
    dateFrom: "",
    dateTo: "",
    search: "",
  });

  const {
    data: requests,
    total,
    loading,
    hasMore,
    page,
    fetch,
    nextPage,
    prevPage,
  } = useCachedFetch({
    cacheKey: "requests",
    endpoint: "/requests/",
    params: filters,
    limit: PAGE_SIZE,
  });

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);
  useEffect(() => {
    fetchRef.current(0);
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) =>
        prev.search === search ? prev : { ...prev, search },
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const toggleSkills = (id) =>
    setExpandedSkills((prev) => ({ ...prev, [id]: !prev[id] }));
  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  /* ── TABLE COLUMNS ── */
  const columns = [
    {
      key: "details",
      header: "Request details",
      width: "minmax(240px, 2fr)",
      skeleton: (
        <div className="req-cell">
          <div className="skeleton sk-title" />
          <div className="skeleton sk-desc" style={{ marginTop: 6 }} />
          <div className="skeleton sk-desc short" style={{ marginTop: 4 }} />
          <div className="skeleton sk-badge" style={{ marginTop: 8 }} />
        </div>
      ),
      render: (req) => {
        const status = statusConfig[req.status] || {
          label: req.status || "Unknown",
          className: "status-unknown",
        };
        return (
          <div className="req-cell">
            <div className="req-title">{req.title || "Untitled Request"}</div>
            <div className="req-desc">{req.description || ""}</div>
            <span className={`req-status-chip ${status.className}`}>
              <span className="material-symbols-outlined chip-icon">
                {statusIcons[req.status] || "cancel"}
              </span>
              {status.label}
            </span>
          </div>
        );
      },
    },
    {
      key: "postedBy",
      header: "Posted by",
      width: "minmax(150px, 1fr)",
      skeleton: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="skeleton skeleton-avatar" />
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div className="skeleton sk-title" style={{ width: 80 }} />
            <div className="skeleton sk-desc" style={{ width: 90 }} />
          </div>
        </div>
      ),
      render: (req) => (
        <div className="posted-by-cell">
          <Avatar
            src={req.userImage}
            sx={{
              width: 32,
              height: 32,
              bgcolor: "#E5E9E5",
              color: "#006663",
              fontSize: "13px",
              flexShrink: 0,
            }}
          >
            {req.created_by?.charAt(0)?.toUpperCase() || "U"}
          </Avatar>
          <div className="posted-by-text">
            <div className="posted-name">{req.created_by || "Unknown"}</div>
            <div className="posted-handle">
              @
              {req.userHandle ||
                req.userName?.toLowerCase().replace(/\s/g, "") ||
                "user"}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "posted",
      header: "Posted date",
      width: "110px",
      skeleton: (
        <div className="req-cell">
          <div className="skeleton sk-title" style={{ width: 90 }} />
          <div
            className="skeleton sk-desc"
            style={{ width: 60, marginTop: 4 }}
          />
        </div>
      ),
      render: (req) => <DateCell value={formatDate(req.created_at)} />,
    },
    {
      key: "delivery",
      header: "Delivery date",
      width: "110px",
      skeleton: (
        <div className="req-cell">
          <div className="skeleton sk-title" style={{ width: 90 }} />
          <div
            className="skeleton sk-desc"
            style={{ width: 60, marginTop: 4 }}
          />
        </div>
      ),
      render: (req) => (
        <DateCell value={formatDate(req.expected_delivery_date)} />
      ),
    },
    {
      key: "budget",
      header: "Budget",
      width: "100px",
      skeleton: (
        <div className="req-cell">
          <div className="skeleton sk-title" style={{ width: 80 }} />
          <div
            className="skeleton sk-desc"
            style={{ width: 50, marginTop: 4 }}
          />
        </div>
      ),
      render: (req) =>
        req.budget ? (
          <div className="req-cell">
            <div className="budget-amount">{formatBudget(req.budget)}</div>
            <div className="budget-type">{req.budgetType || "Fixed"}</div>
          </div>
        ) : (
          <span className="no-val">—</span>
        ),
    },
    {
      key: "category",
      header: "Category & sub-category",
      width: "minmax(150px, 1.2fr)",
      skeleton: (
        <div className="req-cell">
          <div className="skeleton sk-title" style={{ width: 120 }} />
          <div
            className="skeleton sk-desc"
            style={{ width: 80, marginTop: 4 }}
          />
        </div>
      ),
      render: (req) =>
        req.category_id ? (
          <div className="req-cell">
            <div className="cat-name">{req.category_id}</div>
            {req.sub_category_id && (
              <div className="subcat-name">{req.sub_category_id}</div>
            )}
          </div>
        ) : (
          <span className="no-val">—</span>
        ),
    },
    {
      key: "location",
      header: "Location",
      width: "minmax(110px, 0.8fr)",
      skeleton: <div className="skeleton sk-badge" style={{ width: 70 }} />,
      render: (req) =>
        req.type ? (
          <div className="req-cell">
            <span
              className={`location-badge ${isOnline(req.type) ? "loc-online" : "loc-inperson"}`}
            >
              <span className="material-symbols-outlined loc-icon">
                {isOnline(req.type) ? "wifi" : "location_on"}
              </span>
              {isOnline(req.type) ? "Online" : "In-Person"}
            </span>
            {req.location?.address && !isOnline(req.type) && (
              <div className="location-address">{req.location.address}</div>
            )}
          </div>
        ) : (
          <span className="no-val">—</span>
        ),
    },
    {
      key: "skills",
      header: "Skills required",
      width: "minmax(160px, 1.2fr)",
      skeleton: (
        <div style={{ display: "flex", gap: 6 }}>
          <div className="skeleton sk-badge" style={{ width: 70 }} />
          <div className="skeleton sk-badge" style={{ width: 50 }} />
        </div>
      ),
      render: (req) => {
        const skills = Array.isArray(req.keywords) ? req.keywords : [];
        const isExpanded = expandedSkills[req.id];
        const visibleSkills = isExpanded
          ? skills
          : skills.slice(0, SKILLS_VISIBLE);
        const extraCount = skills.length - SKILLS_VISIBLE;
        return (
          <div className="skills-wrap">
            {visibleSkills.map((skill, i) => (
              <span key={i} className="skill-pill">
                {typeof skill === "string" ? skill : skill.name}
              </span>
            ))}
            {extraCount > 0 && (
              <button
                type="button"
                className="skill-pill skill-more"
                onClick={() => toggleSkills(req.id)}
              >
                {isExpanded ? "Show less" : `+${extraCount}`}
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: "offers",
      header: "Offers",
      width: "60px",
      skeleton: <div className="skeleton sk-title" style={{ width: 30 }} />,
      render: (req) =>
        req.offersCount !== undefined ? (
          <div className="offers-cell">
            <span className="offers-count">{req.offersCount}</span>
            <span className="offers-label">offers</span>
          </div>
        ) : (
          <span className="no-val">—</span>
        ),
    },
    {
      key: "action",
      header: "",
      width: "44px",
      align: "end",
      sticky: true, // keeps the view button reachable while the table scrolls sideways
      skeleton: (
        <div
          className="skeleton sk-badge"
          style={{ width: 32, borderRadius: "50%" }}
        />
      ),
      render: (req) => (
        <button
          type="button"
          className="view-btn"
          onClick={() => setSelectedRequest(req)}
          title="View Request"
          aria-label="View request"
        >
          <span className="material-symbols-outlined">visibility</span>
        </button>
      ),
    },
  ];

  /* ── UI ── */
  return (
    <Layout>
      <div className="req-page">
        <PageHeader
          title="Requests"
          description="View and monitor all service requests posted by users. Businesses and freelancers can send quotes on these requests."
        />

        {/* FILTER BAR */}
        <div className="req-filter-bar">
          {/* Search */}
          <div className="req-search-wrapper">
            <span className="material-symbols-outlined req-search-icon">
              search
            </span>
            <input
              type="text"
              className="req-search-input"
              placeholder="Search requests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <span
                className="material-symbols-outlined req-clear-icon"
                onClick={() => setSearch("")}
              >
                close
              </span>
            )}
          </div>

          {/* Status dropdown */}
          <div className="select-wrapper">
            <select
              aria-label="Filter by status"
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
            >
              <option value="all">All Request Statuses</option>
              {statusFilters.map((s) => (
                <option key={s} value={s}>
                  {statusConfig[s]?.label || s}
                </option>
              ))}
            </select>
            <span className="material-symbols-outlined select-arrow">
              expand_more
            </span>
          </div>

          {/* Category dropdown */}
          <div className="select-wrapper">
            <select
              aria-label="Filter by category"
              value={filters.category}
              onChange={(e) => setFilter("category", e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="design">Design & User Experience</option>
              <option value="development">Programming & Tech</option>
              <option value="writing">Writing & Translation</option>
              <option value="marketing">Marketing</option>
              <option value="data">Data Science & Analytics</option>
              <option value="other">Other</option>
            </select>
            <span className="material-symbols-outlined select-arrow">
              expand_more
            </span>
          </div>

          {/* Sub-category dropdown */}
          <div className="select-wrapper">
            <select
              aria-label="Filter by sub-category"
              value={filters.subCategory}
              onChange={(e) => setFilter("subCategory", e.target.value)}
            >
              <option value="all">All Sub-categories</option>
              <option value="ui-ux">UI/UX Design</option>
              <option value="brand-identity">Brand Identity</option>
              <option value="web-development">Web Development</option>
              <option value="seo-writing">SEO Writing</option>
              <option value="python">Python</option>
            </select>
            <span className="material-symbols-outlined select-arrow">
              expand_more
            </span>
          </div>

          {/* Location dropdown */}
          <div className="select-wrapper">
            <select
              aria-label="Filter by location"
              value={filters.location}
              onChange={(e) => setFilter("location", e.target.value)}
            >
              <option value="all">All Locations</option>
              <option value="online">Online</option>
              <option value="in-person">In-Person</option>
            </select>
            <span className="material-symbols-outlined select-arrow">
              expand_more
            </span>
          </div>

          {/* Date range */}
          <div className="req-date-range">
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "15px", color: "#888" }}
            >
              calendar_today
            </span>
            <input
              type="date"
              aria-label="From date"
              className="req-date-input"
              value={filters.dateFrom}
              onChange={(e) => setFilter("dateFrom", e.target.value)}
            />
            <span className="date-sep">–</span>
            <input
              type="date"
              aria-label="To date"
              className="req-date-input"
              value={filters.dateTo}
              onChange={(e) => setFilter("dateTo", e.target.value)}
            />
            {(filters.dateFrom || filters.dateTo) && (
              <span
                className="material-symbols-outlined req-clear-icon"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }))
                }
              >
                close
              </span>
            )}
          </div>

          {/* Filter button */}
          <button type="button" className="req-filter-pill">
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "16px" }}
            >
              tune
            </span>
            Filter
          </button>
        </div>

        {/* SORT ROW */}
        <div className="req-sort-row">
          <span className="sort-label">Sort by:</span>
          <button
            type="button"
            className="sort-toggle"
            onClick={() =>
              setFilter("sort", filters.sort === "latest" ? "oldest" : "latest")
            }
          >
            {filters.sort === "latest" ? "Newest First" : "Oldest First"}
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "15px" }}
            >
              {filters.sort === "latest" ? "arrow_downward" : "arrow_upward"}
            </span>
          </button>
        </div>

        <DataTable
          columns={columns}
          rows={requests}
          loading={loading}
          skeletonRows={10}
          minWidth={1400}
          emptyMessage={
            <>
              <span className="material-symbols-outlined req-empty-icon">
                inbox
              </span>
              <span>No service requests found</span>
            </>
          }
          footer={
            <TablePagination
              page={page}
              limit={PAGE_SIZE}
              total={total}
              count={requests.length}
              hasMore={hasMore}
              loading={loading}
              onPrev={prevPage}
              onNext={nextPage}
              noun="requests"
            />
          }
        />
      </div>

      {/* DETAIL MODAL */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          formatDate={formatDate}
          formatBudget={formatBudget}
        />
      )}
    </Layout>
  );
};

export default CustomersRequests;
